import { AlertCircle, AlertTriangle, CheckCircle2, Info, X, type LucideIcon } from "lucide-react";
import type { CSSProperties, ReactNode } from "react";
import { Toaster as SonnerToaster, toast as sonner } from "sonner";
import { cn } from "~/lib/utils";

type Tone = "success" | "error" | "info" | "warning";

/** Per-tone label, icon and tint (the tint colours live in tokens.css as --toast-*). */
const TONES: Record<Tone, { label: string; Icon: LucideIcon }> = {
  success: { label: "Success", Icon: CheckCircle2 },
  error: { label: "Error", Icon: AlertCircle },
  info: { label: "Info", Icon: Info },
  warning: { label: "Warning", Icon: AlertTriangle },
};

/** How long a toast stays before dismissing itself. */
const AUTO_DISMISS_MS = 6000;

interface ToastOptions {
  /** A button in the toast, e.g. Undo. Clicking it also dismisses the toast. */
  action?: { label: string; onClick: () => void };
  duration?: number;
}

function show(tone: Tone, message: ReactNode, options: ToastOptions = {}) {
  return sonner.custom((id) => <LiquidToast id={id} tone={tone} message={message} {...options} />, {
    // Errors stay until dismissed: often they're the only sign something
    // failed, and one that vanishes mid-read leaves people guessing.
    duration: options.duration ?? (tone === "error" ? Infinity : AUTO_DISMISS_MS),
  });
}

/** App toasts: `toast.success("Saved.")`, `toast.error(message)`, with an optional action. */
export const toast = {
  success: (message: ReactNode, options?: ToastOptions) => show("success", message, options),
  error: (message: ReactNode, options?: ToastOptions) => show("error", message, options),
  info: (message: ReactNode, options?: ToastOptions) => show("info", message, options),
  warning: (message: ReactNode, options?: ToastOptions) => show("warning", message, options),
  dismiss: (id?: string | number) => sonner.dismiss(id),
};

/** A tinted pane of liquid glass: backdrop blur, tone gradient, and a sheen along the top edge. */
function LiquidToast({
  id,
  tone,
  message,
  action,
}: {
  id: string | number;
  tone: Tone;
  message: ReactNode;
  action?: ToastOptions["action"];
}) {
  const { label, Icon } = TONES[tone];
  return (
    <div
      className="toast-liquid relative w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-2xl text-white"
      style={{ "--toast-tone": `var(--toast-${tone})` } as CSSProperties}
    >
      <div aria-hidden className="toast-liquid-sheen absolute inset-x-0 top-0 h-1/2" />
      <div aria-hidden className="absolute inset-x-6 top-0 h-px bg-white/60 blur-[0.5px]" />
      <div className="relative flex items-start gap-3 px-4 py-3.5">
        <Icon className="mt-0.5 size-5 shrink-0 drop-shadow-sm" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-body font-semibold drop-shadow-sm">{label}</p>
          <p className="mt-0.5 text-body break-words text-white/95">{message}</p>
          {action && (
            <button
              type="button"
              onClick={() => {
                action.onClick();
                sonner.dismiss(id);
              }}
              className={cn(
                "mt-2.5 rounded-full bg-white/20 px-3 py-1 text-small font-semibold ring-1 ring-white/35 ring-inset",
                "transition-colors hover:bg-white/30 focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none",
              )}
            >
              {action.label}
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => sonner.dismiss(id)}
          aria-label="Dismiss notification"
          className="-mt-1 -mr-1 shrink-0 rounded-full p-1.5 text-white/85 transition-colors hover:bg-white/20 hover:text-white focus-visible:ring-2 focus-visible:ring-white focus-visible:outline-none"
        >
          <X className="size-4" />
        </button>
      </div>
    </div>
  );
}

export function Toaster() {
  return (
    <SonnerToaster
      position="top-right"
      gap={10}
      offset={16}
      mobileOffset={{ top: 12, left: 16, right: 16 }}
      toastOptions={{ unstyled: true }}
    />
  );
}
